import React, { Component } from 'react';
import classNames from 'classnames';
import { withDimensions } from '../../util/uiHelpers';

import Handle from './Handle';
import Track from './Track';
import css from './RangeSlider.module.css';

// Buffer táctil por defecto del tope (Handle.module.css: 44px).
const DEFAULT_TOUCH_BUFFER = 44;

class RangeSliderComponent extends Component {
  constructor(props) {
    super(props);

    const { min, max, handles, minSeparation = 0 } = props;
    handles.forEach((h, index) => {
      if (
        h < min ||
        h > max ||
        (index < handles.length - 1 && h > handles[index + 1] - minSeparation)
      ) {
        throw new Error(
          'RangeSlider error: handles need to be given in ascending order and they need to be within min and max values'
        );
      }
    });

    this.state = { activeHandle: 0 };

    this.toPosition = this.toPosition.bind(this);
    this.toValue = this.toValue.bind(this);
    this.changeActive = this.changeActive.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onTrackSelect = this.onTrackSelect.bind(this);
  }

  toPosition(value) {
    const { dimensions, min = 0, max = 10000000 } = this.props;
    const width = dimensions.width;
    const valueOffset = value - min;
    const scale = max - min;
    return Math.round((valueOffset / scale) * width);
  }

  toValue(position) {
    const { dimensions, min = 0, max = 10000000, step = 1 } = this.props;
    const width = dimensions.width;
    const scale = max - min;
    const value = Math.round((position / width) * scale) + min;
    return Math.ceil(value / step) * step;
  }

  changeActive(index) {
    this.setState({ activeHandle: index });
  }

  onChange(position, handleIndex) {
    this.props.onChange(Object.assign([...this.props.handles], { [handleIndex]: position }));
  }

  /**
   * FASE2-WIZARD-2b-bis: pulsar en cualquier punto de la barra lleva el tope
   * más cercano a esa posición. Es el comportamiento estándar de un deslizador
   * de dos topes y en móvil resulta imprescindible: arrastrar con el dedo un
   * tope de 44px es mucho más costoso que tocar donde se quiere.
   */
  onTrackSelect(e) {
    // El clic que nace en un tope lo gestiona el propio tope (arrastre).
    if (e.target.closest && e.target.closest('[data-slider-handle]')) {
      return;
    }
    const { handles, min = 0, max = 10000000, minSeparation = 0 } = this.props;
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.touches?.[0]?.clientX ?? e.clientX;
    if (clientX == null) {
      return;
    }
    const value = Math.min(Math.max(this.toValue(clientX - rect.left), min), max);

    // Tope más cercano. En empate gana el que TENGA recorrido hacia el punto
    // pulsado: con los dos juntos, mover el que está bloqueado no haría nada y
    // el control parecería muerto.
    const distances = handles.map(h => Math.abs(h - value));
    const best = distances.reduce((bestIndex, d, i) => {
      if (d < distances[bestIndex]) return i;
      if (d > distances[bestIndex]) return bestIndex;
      // Empate: el de la izquierda si el punto queda a su izquierda.
      return value < handles[i] ? Math.min(bestIndex, i) : Math.max(bestIndex, i);
    }, 0);

    // No se permite cruzar el otro tope, ni acercarse más de lo que
    // permita minSeparation.
    const lower = best === 0 ? min : handles[best - 1] + minSeparation;
    const upper = best === handles.length - 1 ? max : handles[best + 1] - minSeparation;
    const clamped = Math.min(Math.max(value, lower), upper);
    if (clamped !== handles[best]) {
      this.changeActive(best);
      this.onChange(clamped, best);
    }
  }

  render() {
    const {
      handles,
      min = 0,
      max = 10000000,
      ariaLabels = [],
      minSeparation = 0,
      marks = [],
      dimensions,
    } = this.props;

    // FASE2-WIZARD-2b-ter: con posiciones discretas y muy juntas, dos buffers
    // táctiles de 44px se pisan y el tope que se renderiza después le roba el
    // toque al otro. En facturación a 375px las fronteras quedan a 33px: los
    // buffers se solapaban 11px justo cuando los topes están contiguos, que es
    // cuando más falta hace poder separarlos.
    //
    // El buffer se recorta a la separación entre posiciones, o sea que su
    // radio pasa a ser la MITAD de esa separación: cada punto de la barra cae
    // en el tope más cercano, sin solape y sin zona muerta entre medias.
    //
    // Solo se aplica cuando hay `marks`, es decir cuando las posiciones son
    // discretas y contadas. En un deslizador continuo —el filtro del buscador,
    // FieldRangeSlider— la separación entre pasos es de fracciones de píxel y
    // recortar el buffer lo dejaría intocable.
    const gapPx =
      marks.length > 1 && dimensions?.width ? dimensions.width / (marks.length - 1) : null;
    const touchBuffer = gapPx != null && gapPx < DEFAULT_TOUCH_BUFFER ? Math.round(gapPx) : null;

    return (
      <Track handles={handles} valueToPosition={this.toPosition} onTrackSelect={this.onTrackSelect}>
        {/* FASE2-WIZARD-2b-ter: marcas en las posiciones donde el tope puede
            posarse. Van en porcentaje, no en píxeles: el ancho medido solo lo
            conoce el componente tras montarse, y una marca desplazada durante
            el primer render se ve saltar. Sin `marks` no se pinta ninguna, así
            que los demás usos del deslizador quedan intactos. */}
        {marks.map(value => (
          <span
            key={`mark_${value}`}
            className={css.mark}
            style={{ left: `${((value - min) / (max - min)) * 100}%` }}
          />
        ))}
        {handles.map((h, index) => {
          // FASE2-WIZARD-2b-bis: un tope sin recorrido (su min y su max
          // coinciden) no debe capturar el puntero. Con los dos topes juntos en
          // el extremo superior, el derecho queda inmóvil y, al renderizarse
          // después, tapaba al izquierdo: el control se quedaba atascado sin
          // forma de separarlos. Ignorando el puntero en el que no puede
          // moverse, el de debajo vuelve a ser agarrable.
          const handleMin = index === 0 ? min : handles[index - 1] + minSeparation;
          const handleMax = index === handles.length - 1 ? max : handles[index + 1] - minSeparation;
          const classes = classNames({
            [css.activeHandle]: this.state.activeHandle === index,
            [css.lockedHandle]: handleMin === handleMax,
          });
          return (
            <Handle
              key={index}
              className={classes}
              value={h}
              step={this.props.step || 1}
              min={handleMin}
              max={handleMax}
              ariaLabel={ariaLabels[index]}
              valueToPosition={this.toPosition}
              positionToValue={this.toValue}
              changeActive={() => this.changeActive(index)}
              onChange={value => this.onChange(value, index)}
              touchBuffer={touchBuffer}
            />
          );
        })}
      </Track>
    );
  }
}

const RangeSliderComponentWithDimensions = withDimensions(RangeSliderComponent);

/**
 * A component that renders a range slider.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {Array<number>} props.handles - The handles to render
 * @param {number} props.min - The minimum value of the range slider
 * @param {number} props.max - The maximum value of the range slider
 * @param {number} props.step - The step value of the range slider
 * @param {number} [props.minSeparation] - Minimum gap between adjacent handles (default 0)
 * @param {Array<number>} [props.marks] - Values to render a tick mark on (default none)
 * @returns {JSX.Element}
 */
const RangeSlider = props => {
  const { rootClassName, className, ...rest } = props;
  const classes = classNames(rootClassName || css.root, className);
  return (
    <div className={classes}>
      <RangeSliderComponentWithDimensions {...rest} />
    </div>
  );
};

export default RangeSlider;
